/**
 * Sticky header for the per-device page. Back to session, device label,
 * inspection-status chip.
 */

import React from 'react';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import type { CheckInDraftJob } from '@techgitter/contracts';
import { Chip, type ChipTone } from '../../chip';
import { DeviceTypeIcon, checkInDeviceLabel } from '../deviceTypes';

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Inspection: not started',
  in_progress: 'Inspection: in progress',
  complete: 'Inspection: complete',
};

// Same tones as the Inspection entry card, so one state reads one way.
const STATUS_TONE: Record<string, ChipTone> = {
  not_started: 'neutral',
  in_progress: 'warning',
  complete: 'success',
};

export interface DeviceDetailHeaderProps {
  draftJob: CheckInDraftJob;
  deviceIndex: number;
  onBack: () => void;
}

/** Same chrome as CheckInSessionHeader (`.ci-header*` in checkInSession.css). */
export const DeviceDetailHeader: React.FC<DeviceDetailHeaderProps> = ({
  draftJob,
  deviceIndex,
  onBack,
}) => {
  const status = draftJob.inspectionStatus;
  return (
    <div className="checkin-sticky-top-header ci-device-header">
      <div className="ci-header ci-centre">
        <button type="button" className="ci-header__back" onClick={onBack} aria-label="Back to session">
          <IconArrowLeft size={18} stroke={1.5} />
        </button>
        <div className="ci-header__main">
          <div className="ci-header__toprow">
            <span style={{ display: 'flex', color: '#555555' }}>
              <DeviceTypeIcon deviceType={draftJob.deviceType} size={18} />
            </span>
            <span className="ci-header__number">Device {deviceIndex + 1}</span>
            <Chip tone="neutral">{checkInDeviceLabel(draftJob)}</Chip>
            <Chip tone={STATUS_TONE[status] ?? 'neutral'}>
              {status === 'complete' && <IconCheck size={12} stroke={2} />}
              {STATUS_LABEL[status] ?? status}
            </Chip>
          </div>
          {draftJob.serviceSummary && (
            <div className="ci-header__customer">{draftJob.serviceSummary}</div>
          )}
        </div>
      </div>
    </div>
  );
};
